import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { getNotificationSettings, updateNotificationSettings } from '../../api/client';
import { toast } from 'sonner';
import { Mail, Bell, Save } from 'lucide-react';

export const AdminNotifications = () => {
  const { admin } = useAdminAuth();
  const [settings, setSettings] = useState({
    recipient_email: '',
    notify_on_enquiry: true,
    notify_on_contact: true,
    notify_on_lead: true
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const isSuperAdmin = admin?.role === 'super_admin';

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await getNotificationSettings();
        setSettings(data);
      } catch {
        // Non-critical — defaults will be used
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can modify notification settings');
      return;
    }
    setSaving(true);
    try {
      await updateNotificationSettings(settings);
      toast.success('Notification settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-green-600 border-r-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-notifications-page">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Notification Settings</h2>
        <p className="text-gray-500 mt-1">Configure email notifications for the summit</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail size={20} />
            Email Recipient
          </CardTitle>
          <CardDescription>
            All notification emails will be sent to this address
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="recipient_email">Recipient Email</Label>
            <Input
              id="recipient_email"
              data-testid="notification-email-input"
              type="email"
              value={settings.recipient_email}
              onChange={(e) => setSettings({ ...settings, recipient_email: e.target.value })}
              disabled={!isSuperAdmin}
              className="mt-2"
              placeholder="admin@example.com"
            />
          </div>
          {!isSuperAdmin && (
            <p className="text-sm text-amber-600">
              Only the Super Admin can modify notification settings.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell size={20} />
            Notification Triggers
          </CardTitle>
          <CardDescription>
            Choose which events trigger email notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="notify-enquiry-toggle"
              checked={settings.notify_on_enquiry}
              onChange={(e) => setSettings({ ...settings, notify_on_enquiry: e.target.checked })}
              disabled={!isSuperAdmin}
              className="w-4 h-4 text-green-600 rounded"
            />
            <div>
              <p className="font-medium text-gray-900">Exhibitor Enquiries</p>
              <p className="text-sm text-gray-500">Get notified when someone submits an exhibitor enquiry</p>
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="notify-contact-toggle"
              checked={settings.notify_on_contact}
              onChange={(e) => setSettings({ ...settings, notify_on_contact: e.target.checked })}
              disabled={!isSuperAdmin}
              className="w-4 h-4 text-green-600 rounded"
            />
            <div>
              <p className="font-medium text-gray-900">Contact Messages</p>
              <p className="text-sm text-gray-500">Get notified when someone sends a contact form message</p>
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="notify-lead-toggle"
              checked={settings.notify_on_lead}
              onChange={(e) => setSettings({ ...settings, notify_on_lead: e.target.checked })}
              disabled={!isSuperAdmin}
              className="w-4 h-4 text-green-600 rounded"
            />
            <div>
              <p className="font-medium text-gray-900">New Lead Captures</p>
              <p className="text-sm text-gray-500">Get notified when a new lead is captured via brochure download</p>
            </div>
          </label>
        </CardContent>
      </Card>

      {isSuperAdmin && (
        <Button
          data-testid="save-notifications-btn"
          onClick={handleSave}
          disabled={saving}
          className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
        >
          <Save size={16} />
          {saving ? 'Saving...' : 'Save Notification Settings'}
        </Button>
      )}

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <p className="text-sm text-blue-800">
            <strong>Email Provider:</strong> Resend (transactional emails)<br />
            <strong>Sender:</strong> onboarding@resend.dev (test mode)<br />
            <strong>Note:</strong> In test mode, emails are only delivered to verified addresses on Resend.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
